/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { styled } from "@mui/material";
import React from "react";

import { useBackgroundColor } from "./contexts/BackgroundContext";
import { BOTTOM_NAVIGATION_HEIGHT, PAGE_PADDING_SIDE, PAGE_PADDING_TOP } from "./util/consts";
import TestIds from "./testIds";

const ResponsivePageBody = styled("div")(({ theme }) => {
  return {
    display: "flex",
    width: "100%",
    height: "fit-content",
    minHeight: "100%",
    padding: `calc(${PAGE_PADDING_TOP.xl}px)  ${PAGE_PADDING_SIDE.xl}px`,

    [theme.breakpoints.down("xl")]: {
      padding: `calc(${PAGE_PADDING_TOP.lg}px)  ${PAGE_PADDING_SIDE.lg}px`,
    },

    [theme.breakpoints.down("lg")]: {
      padding: `calc(${PAGE_PADDING_TOP.md}px)  ${PAGE_PADDING_SIDE.md}px`,
    },

    [theme.breakpoints.down("md")]: {
      padding: `calc(${PAGE_PADDING_TOP.sm}px)  ${PAGE_PADDING_SIDE.sm}px`,
    },

    [theme.breakpoints.down("sm")]: {
      padding: `calc(${PAGE_PADDING_TOP.xs}px)  ${PAGE_PADDING_SIDE.xs}px`,
    },

    [theme.breakpoints.down("xs")]: {
      paddingBottom: `calc(${BOTTOM_NAVIGATION_HEIGHT}px + 1rem + var(--app-safe-area-bottom))`,
    },

    [theme.breakpoints.up("md")]: {
      paddingLeft: "1.25rem",
    },
  };
});

export const ResponsivePageLayout = styled("div", {
  shouldForwardProp: (prop) => prop !== "backgroundColor",
})<{ backgroundColor?: string }>(({ theme, backgroundColor }) => ({
  display: "flex",
  flexGrow: 1,
  overflow: "auto",
  overflowX: "hidden",
  [theme.breakpoints.up("md")]: {
    paddingLeft: "var(--sidenav-gutter)",
  },
  [theme.breakpoints.between("md", "lg")]: {
    height: "calc(var(--doc-height) - var(--sidenav-inset) * 2)",
  },
  [theme.breakpoints.down("md")]: {
    padding: `0 0 calc(${BOTTOM_NAVIGATION_HEIGHT}px + var(--app-safe-area-bottom)) 0`,
  },
  ...(backgroundColor ? { backgroundColor } : {}),
}));

export const PageLayout: React.FC<{ children: React.ReactNode }> = (props) => {
  const { backgroundColor } = useBackgroundColor();

  return (
    <ResponsivePageLayout
      backgroundColor={backgroundColor}
      id={TestIds.pages.mainScrollContainer}
      data-testid={TestIds.pages.mainScrollContainer}
      className={TestIds.pages.mainScrollContainer}
      role="main"
    >
      <ResponsivePageBody>{props.children}</ResponsivePageBody>
    </ResponsivePageLayout>
  );
};
